import React, { useState } from "react";
import { FileText, CheckCircle2, Eye } from "lucide-react";
import { useToast } from "@/contexts/ToastContext"; 

function PassengerDocuments({
  documents,
  passengerName,
  onExtract,
  loading,
  dataExtracted,
  extractedCount,
}) {
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [viewingDocument, setViewingDocument] = useState(null);
   const { showToast } = useToast();

  const handleFileChange = (documentTypeId, file, documentTypeName) => {
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    const allowedExtensions = ["pdf", "jpg", "jpeg", "png"];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!allowedTypes.includes(file.type) ||
      !allowedExtensions.includes(fileExtension)) {
      showToast("Only PDF, JPG, and PNG files are allowed.");
      return;
    }

    const fileData = {
      id: documentTypeId,
      name: file.name,
      documentType: documentTypeName,
      file: file,
      status: "uploaded",
      previewUrl: URL.createObjectURL(file),
    };

    setUploadedFiles((prev) => ({
      ...prev,
      [documentTypeId]: fileData,
    }));
  };


  const handleUploadAndExtract = async () => {
    if (Object.keys(uploadedFiles).length === 0) {
      alert("Please upload at least one document");
      return;
    }

    // Convert to array format for display
    const uploadedDocsArray = Object.values(uploadedFiles);

    // Pass both formats: object for API, array for display
    await onExtract(uploadedFiles, uploadedDocsArray);
  };

  const handleViewDocument = (fileData) => {
    setViewingDocument(fileData);
  };

  const closeViewer = () => {
    setViewingDocument(null);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">
          📄 Upload Required Documents
        </h4>
        <p className="text-sm text-blue-800">
          Upload documents for <strong>{passengerName || "this passenger"}</strong>
        </p>
      </div>

      <div className="space-y-3">
        {documents.map((doc) => {
          const uploaded = uploadedFiles[doc.id];

          return (
            <div
              key={doc.id}
              className="border rounded-lg p-4 hover:border-blue-300 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <label className="font-medium text-gray-900 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-600" />
                  {doc.name}
                  {doc.is_mandatory && (
                    <span className="text-red-500 text-sm">*</span>
                  )}
                </label>
                <div className="flex items-center gap-2">
                  {uploaded && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleViewDocument(uploaded)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="View document"
                      >
                        <Eye className="w-5 h-5 text-blue-600" />
                      </button>
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    </>
                  )}
                </div>
              </div>

              {doc.description && (
                <p className="text-sm text-gray-600 mb-3">
                  {doc.description}
                  {doc.allowed_formats && (
                    <span className="text-xs text-gray-500 ml-2">
                      ({doc.allowed_formats.join(", ")})
                    </span>
                  )}
                </p>
              )}

              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) =>
                  handleFileChange(doc.id, e.target.files[0], doc.name)
                }
                className="text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />

              {uploaded && (
                <p className="text-xs text-green-600 mt-2">
                  ✓ {uploaded.name} uploaded
                </p>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleUploadAndExtract}
        disabled={loading || Object.keys(uploadedFiles).length === 0}
        className={`w-full py-3 px-6 rounded-xl font-semibold transition-all ${loading || Object.keys(uploadedFiles).length === 0
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg"
          }`}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
            Processing...
          </span>
        ) : dataExtracted ? (
          <span className="flex items-center justify-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            ✨ {extractedCount} Fields Extracted
          </span>
        ) : (
          "Upload & Extract Data"
        )}
      </button>

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg">
                {viewingDocument.documentType}
              </h3>
              <button
                onClick={closeViewer}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {viewingDocument.name.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={viewingDocument.previewUrl}
                  className="w-full h-full min-h-[600px]"
                  title="Document preview"
                />
              ) : (
                <img
                  src={viewingDocument.previewUrl}
                  alt={viewingDocument.documentType}
                  className="max-w-full h-auto mx-auto"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PassengerDocuments;